import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ROUTER_FUTURE_FLAGS } from "./routerFuture";
import { amplifyTheme } from "./amplifyTheme";
import React from "react";
import { signUp } from "aws-amplify/auth";
import { Authenticator, useTheme, View } from "@aws-amplify/ui-react";
import { ThemeProvider } from "@aws-amplify/ui-react";
import CompanyEmailValidator from "company-email-validator";

import { useTheme as useAppTheme } from "../features/theme";
import { ProfileSettingsPage } from "../features/profile";
import { UsagePage } from "../features/usage";
import { SettingsPage } from "../features/settings";
import { InsightsPage, TracesPage } from "../features/insights";
import { FailureClustersPage } from "../features/failure-clusters";
import { EvaluatorsPage } from "../features/evaluators";
import { ImpersonatePage, useImpersonation } from "../features/impersonation";
import { SuppressionsPage } from "../features/suppressions";
import DocumentsPage from "../features/context/DocumentsPage";
import { ScreenSizeCheck, NotFoundPage } from "../components/layout";
import { ProtectedRoute, useAuth } from "../features/auth";
import { Providers } from "./providers";
import { AUTH_PATH } from "../lib/constants";
import { BrandLogo, FullScreenLoader } from "../ui";

const ROUTES = {
  AUTH_PATH: AUTH_PATH,
  INSIGHTS_PAGE: "/insights",
  FAILURE_CLUSTERS_PAGE: "/failure-clusters",
  TRACES_PAGE: "/traces",
  EVALUATORS_PAGE: "/evaluators",
  CONTEXT_PAGE: "/context",
  USAGE_PAGE: "/usage",
  SETTINGS_PAGE: "/settings",
  PROFILE_PAGE: "/profile",
  IMPERSONATE_PAGE: "/impersonate",
  SUPPRESSIONS_PAGE: "/suppressions",
  // Legacy routes kept as redirects to their new homes (016 §4/§7).
  LEGACY_CONTROL_CENTER: "/control-center",
  LEGACY_API_KEYS: "/api-keys",
  LEGACY_RATE_LIMITS: "/rate-limits",
};

// CONFIGURATION FOR AMPLIFY AUTH PAGE
const formFields = {
  signIn: {
    username: { label: "Email", placeholder: "Enter your email" },
    password: { label: "Password", placeholder: "Enter your password" },
  },
  signUp: {
    username: { label: "Work Email", placeholder: "Enter your work email" },
    password: { label: "Password", placeholder: "Enter your password" },
    confirm_password: { label: "Confirm Password", placeholder: "Confirm your password" },
  },
  resetPassword: {
    username: { label: "Email", placeholder: "Enter your email" },
  },
};

const components = {
  Header() {
    const { tokens } = useTheme();

    return (
      <View textAlign="center" padding={tokens.space.large}>
        <BrandLogo className="mx-auto h-12 mb-6 mt-6" />
      </View>
    );
  },
};

const services = {
  async handleSignUp(formData: any) {
    let { username, password } = formData;
    username = username.toLowerCase();

    const isCompanyEmail = CompanyEmailValidator.isCompanyEmail(username);

    if (isCompanyEmail) {
      return signUp({ username, password });
    } else {
      throw new Error("Please sign up with your work email.");
    }
  },
};

// Default authenticated landing → Insights (016 §4); unauthenticated → /auth.
const DefaultRouteNavigator: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <FullScreenLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.AUTH_PATH} replace />;
  }

  return <Navigate to={ROUTES.INSIGHTS_PAGE} replace />;
};

const protect = (element: React.ReactNode) => <ProtectedRoute>{element}</ProtectedRoute>;

// The route tree is remounted whenever the impersonation scope changes (via the
// `key` below). That makes every page re-run its data fetches against the new
// domain — the same "no stale data across scopes" guarantee the old
// window.location.reload() gave, but without a full-page reload. The scope
// itself is persisted (see impersonation/storage.ts) so it survives a real
// refresh and is shared across tabs; this remount just reacts to it changing.
// It lives in its own component so it can read the impersonation context, which
// is provided above it in <Providers>.
const AppRoutes: React.FC = () => {
  const { impersonationDomain } = useImpersonation();
  const { resolvedTheme } = useAppTheme();
  return (
    <Routes key={impersonationDomain ?? "self"}>
      <Route
        path={ROUTES.AUTH_PATH}
        element={
          <ThemeProvider theme={amplifyTheme} colorMode={resolvedTheme}>
            <div className="flex min-h-screen items-center justify-center bg-background px-4">
              <Authenticator
                services={services}
                formFields={formFields}
                components={components}
              >
                <Navigate to="/" replace />
              </Authenticator>
            </div>
          </ThemeProvider>
        }
      />
      <Route path={"/"} element={<DefaultRouteNavigator />} />

      <Route path={ROUTES.INSIGHTS_PAGE} element={protect(<InsightsPage />)} />
      <Route
        path={ROUTES.FAILURE_CLUSTERS_PAGE}
        element={protect(<FailureClustersPage />)}
      />
      <Route path={ROUTES.TRACES_PAGE} element={protect(<TracesPage />)} />
      <Route path={ROUTES.EVALUATORS_PAGE} element={protect(<EvaluatorsPage />)} />
      <Route
        path={`${ROUTES.EVALUATORS_PAGE}/:groupId`}
        element={protect(<EvaluatorsPage />)}
      />
      <Route path={ROUTES.CONTEXT_PAGE} element={protect(<DocumentsPage />)} />
      <Route path={ROUTES.USAGE_PAGE} element={protect(<UsagePage />)} />
      <Route path={ROUTES.SETTINGS_PAGE} element={protect(<SettingsPage />)} />
      <Route path={ROUTES.PROFILE_PAGE} element={protect(<ProfileSettingsPage />)} />
      <Route
        path={ROUTES.IMPERSONATE_PAGE}
        element={protect(<ImpersonatePage />)}
      />
      <Route
        path={ROUTES.SUPPRESSIONS_PAGE}
        element={protect(<SuppressionsPage />)}
      />

      {/* Legacy routes → new homes (no dead bookmarks). */}
      <Route
        path={ROUTES.LEGACY_CONTROL_CENTER}
        element={<Navigate to={ROUTES.PROFILE_PAGE} replace />}
      />
      <Route
        path={ROUTES.LEGACY_API_KEYS}
        element={<Navigate to={ROUTES.SETTINGS_PAGE} replace />}
      />
      <Route
        path={ROUTES.LEGACY_RATE_LIMITS}
        element={<Navigate to={ROUTES.USAGE_PAGE} replace />}
      />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

const AppRouter: React.FC = () => {
  return (
    <ScreenSizeCheck>
      <Providers>
        <Router future={ROUTER_FUTURE_FLAGS}>
          <AppRoutes />
        </Router>
      </Providers>
    </ScreenSizeCheck>
  );
};

export default AppRouter;
